import { Link } from "react-router-dom";
import { ArrowRight, UserPlus, Hourglass, CalendarDays, Check, Clock } from "lucide-react";
import GameCard from "@/components/GameCard";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Avatar, EmptyState } from "@/components/Bits";
import { signed } from "@/lib/share";
import { timeLeft } from "@/pages/league/OverviewTab";
import { eventText } from "@/pages/league/ActivityTab";
import AdSlot from "@/components/AdSlot";

const MIN_MEMBERS = 4;

function Waiting({ L }) {
  const missing = Math.max(0, MIN_MEMBERS - L.member_count);
  return (
    <section className="mpl-card mpl-feature p-6" data-testid="vh-waiting">
      <p className="mpl-eyebrow">Ligue en attente</p>
      <p className="mt-2 font-display text-3xl font-extrabold uppercase leading-none sm:text-4xl">Encore {missing} membre{missing > 1 ? "s" : ""} pour lancer la ligue</p>
      <p className="mt-2 text-sm text-slate-300">{L.member_count}{L.max_members ? `/${L.max_members}` : ""} membres. La compétition, le classement et les rivalités démarrent à {MIN_MEMBERS} membres.</p>
      {L.is_owner && !L.archived && !L.suspended && <Link to={`/ligues/${L.id}/invitations`} className="mpl-btn-primary mt-5" data-testid="vh-invite"><UserPlus size={15} /> Inviter des membres</Link>}
    </section>
  );
}

function Challenge({ d }) {
  const c = d.challenge;
  const ps = d.challenge_participants || [];
  return (
    <section className={`mpl-card mpl-feature relative overflow-hidden p-6 ${c ? "min-h-[300px]" : ""}`} data-testid="vh-challenge">
      <div className="mpl-photo mpl-photo-player" aria-hidden="true" />
      <span className="mpl-photo-caption">Illustration</span>
      <div className="relative z-[1] max-w-md">
        <p className="mpl-eyebrow">{c ? "Le défi en cours" : "Aucun défi en cours"}</p>
        {c ? (<>
          <p className="mt-2 font-display text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl" data-testid="vh-challenge-title">{c.title}</p>
          <p className="mt-2 text-sm text-slate-200">{c.consequence}</p>
          {ps.length > 0 && <div className="mt-4 flex flex-wrap gap-1.5" data-testid="vh-challenge-participants">{ps.slice(0, 8).map((p) => (
            <span key={p.id} className="vh-participant relative rounded-full" title={`${p.display_name} : ${p.acceptance === "accepted" ? "a accepté" : p.acceptance === "declined" ? "a refusé" : "en attente"}`}><Avatar user={p} size={36} /></span>))}</div>}
          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-300">{ps.length} participant{ps.length > 1 ? "s" : ""} · {c.period_label}
            {c.status === "active" ? <span className="flex items-center gap-1 font-semibold text-[#B7F52A]"><Clock size={12} /> Fin dans {timeLeft(c.end)}</span>
              : <span className="font-semibold text-amber-200">{c.status === "pending" ? "En attente d'acceptation" : "Commence bientôt"}</span>}</p>
        </>) : <p className="mt-2 text-sm text-slate-300">Propose un défi volontaire à ta ligue : le dernier de la période le relève.</p>}
        {d.challenges_to_answer > 0 && <p className="mt-2 text-xs font-bold text-amber-200" data-testid="vh-challenges-pending">{d.challenges_to_answer} défi(s) attendent ta réponse</p>}
        {(c || (!d.league.archived && !d.league.suspended)) && <Link to={`/ligues/${d.league.id}/defis`} className="mpl-btn-primary mt-5" data-testid="vh-challenge-link">{c ? "Voir le défi" : "Lancer un défi"} <ArrowRight size={15} /></Link>}
      </div>
    </section>
  );
}

function Race({ d, me }) {
  return (
    <section className="mpl-card p-5" data-testid="vh-race">
      <p className="mpl-mark font-display text-xl font-extrabold uppercase">La course</p>
      <ol className="mt-3 divide-y divide-white/[0.06]">{d.top.map((r, i) => (
        <li key={r.user_id} className={`flex items-center gap-3 py-2.5 ${r.user_id === me ? "text-[#B7F52A]" : ""}`} data-testid={`vh-race-${r.rank}`}>
          <span className="w-5 font-mono-num text-sm text-slate-400">{r.rank}</span>
          <Avatar user={r} size={32} />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{r.user_id === me ? "Toi" : r.display_name}</span>
          <span className={`font-mono-num text-lg font-bold ${i === 0 ? "mpl-score-win" : ""}`}>{signed(r.points || 0)}</span>
        </li>))}</ol>
      <Link to={`/ligues/${d.league.id}/classement`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#B7F52A]" data-testid="vh-race-link">Classement complet <ArrowRight size={13} /></Link>
    </section>
  );
}

function Rival({ d }) {
  const r = d.rival;
  return (
    <section className="mpl-card p-5" data-testid="vh-rival">
      <p className="mpl-mark font-display text-xl font-extrabold uppercase">{r.weekly ? "Ton rival de la semaine" : "Ton rival du moment"}</p>
      <div className="mt-3 flex items-center gap-3"><Avatar user={r} size={52} />
        <div><p className="font-display text-2xl font-extrabold uppercase leading-none">{r.display_name}</p>
          <p className="text-xs text-slate-300">{r.ahead ? `${r.rank}e, devant toi.` : "Derrière toi."}</p></div></div>
      <p className="mt-4 font-display text-4xl font-extrabold uppercase leading-none" data-testid="vh-rival-gap">
        <span className="text-[#B7F52A]">{r.gap} pt{r.gap > 1 ? "s" : ""}</span><br />{r.gap === 0 ? "À égalité." : r.ahead ? "À rattraper." : "D'avance à garder."}</p>
      <Link to={`/ligues/${d.league.id}/membre-${r.user_id}`} className="mpl-btn-ghost mt-4" data-testid="vh-rival-link">Voir le face-à-face <ArrowRight size={14} /></Link>
    </section>
  );
}

export function VestiaireGreeting({ d, user }) {
  const L = d.league;
  return (
      <header className="mpl-rise">
        <h1 className="font-display text-5xl font-extrabold uppercase leading-none sm:text-6xl" data-testid="vh-greeting">Bonjour {user.display_name}</h1>
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-300 [overflow-wrap:anywhere]">
          <Link to={`/ligues/${L.id}`} className="hover:text-[#B7F52A]" data-testid="dashboard-league-name">{L.name}</Link> <VerifiedBadge show={L.verified} size={18} />
          {L.competition_started && d.standing?.rank ? <> · <span data-testid="dashboard-rank">{d.standing.rank}e</span> · <span data-testid="dashboard-points">{signed(d.standing.points || 0)} pts</span></> : null}
        </p>
      </header>
  );
}

export default function DashboardVestiaire({ d, user, games, tz, pick, saving }) {
  const L = d.league;
  return (
    <div className="space-y-5" data-testid="dashboard-vestiaire">
      {!L.competition_started && !L.archived && !L.suspended && <Waiting L={L} />}
      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Challenge d={d} />
        {d.top.length > 0 ? <Race d={d} me={user.id} /> : (
          <section className="mpl-card flex flex-col justify-center p-5" data-testid="vh-race-waiting">
            <p className="mpl-mark font-display text-xl font-extrabold uppercase">La course</p>
            <p className="mt-2 flex items-center gap-2 text-sm text-slate-300"><Hourglass size={14} /> Le classement apparaîtra au lancement de la compétition.</p>
          </section>)}
      </div>
      <AdSlot placement="home" leagueId={L.id} />
      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="mpl-mark font-display text-2xl font-extrabold uppercase">À toi de jouer {d.pending_today > 0 && <span className="ml-2 rounded-md bg-[#B7F52A] px-2 font-sans text-xs text-[#101310]" data-testid="dashboard-pending">{d.pending_today}</span>}</h2>
          <span className="flex shrink-0 items-center gap-3"><Link to="/pronos-rapides" className="rounded-md bg-[#B7F52A] px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#101310]" data-testid="dashboard-quick-link">Pronos rapides</Link>
          <Link to="/matchs" className="text-xs font-semibold uppercase tracking-wider text-slate-300 hover:text-[#B7F52A]" data-testid="dashboard-matches-link">Tous →</Link></span>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {games.length ? games.map((g) => <GameCard key={g.id} game={g} tz={tz} onPick={pick} saving={saving === g.id} showDate />)
            : <EmptyState icon={CalendarDays} title="Aucun match à venir" text="Les prochaines rencontres NBA apparaîtront dès leur publication." testId="dashboard-no-games" />}
          {d.rival && <Rival d={d} />}
        </div>
      </section>
      <section className="mpl-card p-5">
        <div className="mb-2 flex items-center justify-between"><h2 className="mpl-mark font-display text-xl font-extrabold uppercase">Dans la ligue</h2>
          <Link to={`/ligues/${L.id}/activite`} className="text-xs font-semibold text-[#B7F52A]" data-testid="dashboard-activity-link">Fil d'activité →</Link></div>
        {d.activity.length ? d.activity.map((e) => <p key={e.id} className="flex items-start gap-2 border-b border-white/[0.04] py-2 text-sm text-slate-200 last:border-0"><Check size={13} className="mt-1 shrink-0 text-[#B7F52A]" /> {eventText(e)}</p>)
          : <p className="text-sm text-slate-400">Rien pour l'instant.</p>}
      </section>
    </div>
  );
}
